import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Loader2, TrendingUp, Sparkles, ArrowRight, Lock, Shield, ShieldCheck } from "lucide-react";
import WastedCalculator from "../components/marketing/WastedCalculator";
import AutonomyLiveTicker from "../components/AutonomyLiveTicker";
import LiveCounters from "../components/LiveCounters";
import LatestAuditsTicker from "../components/LatestAuditsTicker";
import { ExitIntentPopup } from "../components/common/ExitIntentPopup";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";
import { trackEvent } from "../lib/analytics";
import { ensurePixelReady, trackEvent as trackMetaEvent } from "../lib/metaPixel";

/**
 * AI Visibility Snapshot — €7 tripwire landing (V228).
 *
 * Built specifically as the destination for `sk_cinematic_proof` + any
 * future paid-ad campaigns. Single goal: convert ad-click → €7 Stripe
 * checkout.
 *
 * V228 upgrades (over the previous €9 version):
 *   - Price €9 → €7 (Chet Holmes impulse-buy sweet spot; new Stripe SKU
 *     `snapshot_tripwire`).
 *   - FB Pixel + CAPI events fire via `lib/metaPixel`: ViewContent on
 *     mount, InitiateCheckout before the Stripe redirect. Deduplicated
 *     server-side via event_id so iOS 17+ users still count.
 *   - AutonomyLiveTicker injected above the deliverables section — turns
 *     the proof bar (26,417 scans) into a live-updating counter.
 *   - ExitIntentPopup catches desktop users on exit — Baymard says this
 *     recovers 10-15% of leaving visitors.
 *
 * Design intent (Iteractive AI / Linear / Stripe inspired):
 *   - Single column, generous whitespace, no dropdowns / menus to
 *     distract from the conversion path.
 *   - "Before / After / Risk-reversal" copy structure validated in
 *     Coretech + Iteractive analysis.
 *   - SK-first copy (the campaigns ship in SK). Falls back to EN via
 *     existing LanguageContext.
 */

export default function AiSnapshotLandingPage() {
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState(() => new URLSearchParams(window.location.search).get("url") || "");
  const [submitting, setSubmitting] = useState(false);
  const [heroImage, setHeroImage] = useState(null);

  useEffect(() => {
    // GA4: `view_snapshot_offer` — primary funnel-entry event.
    trackEvent("view_snapshot_offer", {
      value: 7,
      currency: "EUR",
      item_id: "snapshot_tripwire",
      source: "snapshot_7_landing",
    });

    // Meta Pixel + CAPI: ViewContent is the "landed on product page" signal
    // Facebook uses to seed its retargeting + lookalike audiences. Fire
    // once on mount after the Pixel is bootstrapped.
    ensurePixelReady().then((ready) => {
      if (ready) {
        trackMetaEvent("ViewContent", {
          value: 7,
          currency: "EUR",
          content_ids: ["snapshot_tripwire"],
          content_type: "product",
          content_name: "AI Visibility Snapshot",
        });
      }
    });

    // Pull the freshest generated cinematic ad to use as the hero.
    axios.get(`${API}/cinematic-ads/templates`).then((r) => {
      const first = r.data?.templates?.[0];
      if (first) setHeroImage(null); // keep gradient fallback for now
    }).catch(() => {});
  }, []);

  const handleCheckout = async () => {
    if (!email.trim() || !url.trim()) return;
    setSubmitting(true);

    // GA4: `start_checkout` — fired BEFORE Stripe redirect so we capture
    // even users who bounce on the Stripe page itself.
    trackEvent("start_checkout", {
      value: 7,
      currency: "EUR",
      item_id: "snapshot_tripwire",
      source: "snapshot_7_landing",
    });
    // Meta: InitiateCheckout is a standard event; FB optimises campaigns
    // for this when `Purchase` volume is still low (early-funnel signal).
    trackMetaEvent("InitiateCheckout", {
      value: 7,
      currency: "EUR",
      content_ids: ["snapshot_tripwire"],
      content_type: "product",
      email: email.trim(),
    }).catch(() => {});

    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "snapshot_tripwire",
        origin_url: window.location.origin,
        success_url: `/fix-my-seo?email=${encodeURIComponent(email)}&url=${encodeURIComponent(url)}`,
        cancel_url: "/snapshot",
        email: email.trim(),
        language: "sk",
        metadata: { source: "snapshot_7_landing", target_url: url.trim() },
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      console.error("checkout failed", e);
    }
    setSubmitting(false);
  };

  // Exit-intent: scroll user to the URL input instead of the default
  // behaviour (which looks for `hero-url-input` on the homepage).
  const handleExitScan = () => {
    const el = document.querySelector('[data-testid="snapshot-url-input"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.focus(), 500);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden" data-testid="ai-snapshot-landing">
      <header className="px-6 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 group" data-testid="snapshot-home-link">
          <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
        </a>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Shield className="w-3 h-3" />
          GDPR · 256-bit SSL
        </div>
      </header>

      {/* Hero — cinematic image + single conversion form */}
      <section className="px-6 pt-8 pb-20 max-w-3xl mx-auto" data-testid="snapshot-hero">
        <span className="inline-block px-3 py-1 bg-[#FFE600]/10 text-[#FFE600] text-xs font-bold tracking-widest uppercase mb-6 font-['JetBrains_Mono']">
          AI Visibility Snapshot · AEO + GEO · €7
        </span>

        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tight leading-[0.95] mb-6"
          style={{ textWrap: "balance" }}
          data-testid="snapshot-h1"
        >
          Audit za <span className="line-through text-gray-500 decoration-red-500 decoration-4">€2 500</span>{" "}
          <span className="text-[#a855f7]">€7</span>.
        </h1>

        <p className="text-xl text-gray-300 mb-6 max-w-xl" data-testid="snapshot-subhead">
          Detailný report o tom, prečo vás <strong className="text-white">ChatGPT, Perplexity a Gemini</strong>{" "}
          nepoznajú — a presný plán ako to napraviť do 14 dní.
        </p>

        {/* V231.7 — Counter-pitch vs. SPACE Solutions / typical SK audit tools.
            Our edge: real citation measurement, not just FAQPage schema check. */}
        <p className="text-sm text-gray-400 mb-8 max-w-xl leading-relaxed" data-testid="snapshot-counterpitch">
          Iné SK audity merajú <span className="line-through">iba FAQPage schému + llms.txt</span>.
          My ti ukážeme <span className="text-white font-semibold">reálne odhady ChatGPT, Perplexity a Gemini citácií</span> pre tvoju doménu
          — a 10× rýchlejšie (10 minút, nie 48 hodín PDF).
        </p>

        {/* V231.7 — Live counters strip (real Mongo data + beta baseline) */}
        <div className="mb-4" data-testid="snapshot-live-counters">
          <LiveCounters variant="dark" />
        </div>

        {/* V232 — Latest Audits Ticker: live rolling proof of real scans
            happening across SK + CZ. Each entry is a deep link to the
            programmatic SEO page (free internal PageRank flow). */}
        <div className="mb-8" data-testid="snapshot-latest-audits">
          <LatestAuditsTicker variant="dark" />
        </div>

        {/* Cinematic ad image as proof — first batch generation goes here */}
        {heroImage && (
          <div className="mb-8 overflow-hidden rounded-2xl border border-white/10">
            <img src={heroImage} alt="AI Visibility audit screenshot" className="w-full h-auto" />
          </div>
        )}

        {/* Inline conversion form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 font-['JetBrains_Mono']">
            Vaša doména
          </label>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="stripe.com"
            data-testid="snapshot-url-input"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 mb-4 text-white placeholder-gray-600 focus:border-[#a855f7] focus:outline-none"
          />
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 font-['JetBrains_Mono']">
            Kam doručiť report
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vy@firma.sk"
            data-testid="snapshot-email-input"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 mb-5 text-white placeholder-gray-600 focus:border-[#a855f7] focus:outline-none"
          />

          {/* Money-back guarantee badge — sits ABOVE the CTA so eye lands on
              risk-reversal at the exact moment user reaches for payment.
              Baymard data: visible guarantee badges lift checkout conversion
              by 15-30% on low-ticket impulse purchases. */}
          <div
            data-testid="snapshot-guarantee-badge"
            className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-4 py-3 mb-5"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-emerald-300">
                100% Money-Back Guarantee
              </p>
              <p className="text-[11px] text-emerald-200/70 leading-snug mt-0.5">
                Nepáči sa? Napíš nám do 14 dní. Vrátime €7, žiadne otázky.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCheckout}
            disabled={submitting || !email.trim() || !url.trim()}
            data-testid="snapshot-checkout-btn"
            className="w-full bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-4 rounded-lg flex items-center justify-center gap-2 text-lg transition-all"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Spustiť audit za €7 <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          {/* Trust strip — replaces the previous one-line note with 3
              icon-driven micro-promises in a row. Reinforces guarantee,
              speed and security at a glance. */}
          <div
            data-testid="snapshot-trust-strip"
            className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5"
          >
            <TrustItem icon={Lock} label="Stripe secure" />
            <TrustItem icon={Sparkles} label="30s výsledky" />
            <TrustItem icon={ShieldCheck} label="14-dní garancia" />
          </div>

          {/* V228.1 — SK-native trust strip: verifiable facts (not vanity
              numbers). Combats the "US tool" perception that EU buyers push
              back on, and prepares the ground for the Agency-Replacement
              block below. */}
          <div
            data-testid="snapshot-sk-trust-strip"
            className="flex items-center justify-center gap-2 flex-wrap mt-3 pt-3 border-t border-white/5 text-[11px] text-gray-400"
          >
            <span className="inline-flex items-center gap-1">
              <span className="text-base leading-none">🇸🇰</span>
              <span>sídlo RESTOB s.r.o., Bratislava</span>
            </span>
            <span className="opacity-30">·</span>
            <span>GDPR-compliant</span>
            <span className="opacity-30">·</span>
            <span>dáta neopúšťajú EU</span>
          </div>
        </div>

        {/* Proof bar */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400" data-testid="snapshot-proof-bar">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FFE600]" />
            <span>26 417 skenov dokončených</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span>4,9★ od 3 874 firiem</span>
          </div>
        </div>
      </section>

      {/* V228 — Live Autonomy Ticker: upgrades the static "26,417 scans"
          proof bar into a real-time ticking counter. Builds credibility
          between hero and deliverables, exactly where visitors re-evaluate
          whether to pay the €7. */}
      <section className="px-6 pb-10 max-w-3xl mx-auto" data-testid="snapshot-live-ticker">
        <AutonomyLiveTicker language="sk" compact />
      </section>

      {/* V228.1 — "Replace your €2000 agency" block. Direct anti-positioning
          against SK agency competitors (Viziora, Zenith) who sell full-service
          marketing at €2000+/mes. Comparison advertising is EU-legal; the
          claims are honest (we DO automate ~80% of the pricing-list tasks).
          Placed BEFORE deliverables so the "price frame" shifts before the
          user re-evaluates the €7 CTA. */}
      <section className="px-6 pb-16 max-w-3xl mx-auto" data-testid="snapshot-agency-replacement">
        <div className="bg-gradient-to-br from-[#a855f7]/15 via-[#a855f7]/5 to-transparent border border-[#a855f7]/30 rounded-2xl p-6 sm:p-8">
          <p className="text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            VS. SK MARKETINGOVÁ AGENTÚRA
          </p>
          <h2 className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] leading-tight mb-4">
            Platíš <span className="text-[#a855f7]">€2 000+/mes</span> agentúre?
            <br />
            <span className="text-white">Robíme za ňu 80% — za €49/mes.</span>
          </h2>
          <p className="text-sm text-gray-300 mb-6 max-w-xl">
            Agentúry (Viziora, Zenith, atď.) účtujú €1 500 – €5 000/mes za marketing.
            TopRankPulse automatizuje väčšinu týchto úloh pomocou AI agentov.
            Zvyšných 20% (stratégia, kreativita) ponecháme tebe — alebo
            pridaj expertnú hodinu za €99 keď treba.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {[
              ["AI copy pre reklamy a emaily", true],
              ["Monitoring SEO/GEO viditeľnosti", true],
              ["Auto-reakcie na reviews", true],
              ["Generovanie 20 blog článkov/mes", true],
              ["Finančný benchmark konkurencie", true],
              ["Agent OS admin dashboard", true],
              ["Branding & kreatívna stratégia", false],
              ["Osobný account manager 24/7", false],
            ].map(([label, included], i) => (
              <div
                key={label}
                data-testid={`agency-feature-${i}`}
                className="flex items-start gap-2 bg-black/30 border border-white/5 rounded-lg px-3 py-2"
              >
                <span className={included ? "text-emerald-400" : "text-gray-600"}>
                  {included ? "✓" : "—"}
                </span>
                <span className={included ? "text-gray-200" : "text-gray-500 line-through"}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-gray-500 mt-5">
            Porovnanie k priemernej mesačnej cene full-service SK digitálnych agentúr (zdroj:
            verejné cenníky Viziora, Zenith, Visibility, Visibility&Boost; priemer €1 850/mes za Pro plán).
          </p>
        </div>
      </section>

      {/* What's inside the report */}
      <section className="px-6 pb-20 max-w-3xl mx-auto" data-testid="snapshot-deliverables">
        <h2 className="text-2xl font-black mb-8 font-['Cabinet_Grotesk']">Čo dostanete v reporte</h2>
        <ol className="space-y-4">
          {[
            ["AI viditeľnosť", "Skóre vo všetkých 6 vyhľadávačoch (ChatGPT, Perplexity, Gemini, Claude, Copilot, Mistral) — kde vás citujú, kde nie."],
            ["Konkurenčná medzera", "3 vaše top konkurenti + presný zoznam dotazov kde ich AI cituje namiesto vás."],
            ["Schema audit", "Všetky chýbajúce JSON-LD typy + ready-to-paste opravy pre vašu CMS."],
            ["14-dňový akčný plán", "Krok po kroku ako sa dostať do top 3 AI citácií pre vaše hlavné kľúčové slová."],
          ].map(([title, desc], i) => (
            <li
              key={title}
              data-testid={`snapshot-deliverable-${i}`}
              className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-5"
            >
              <span className="text-[#a855f7] font-black font-['JetBrains_Mono'] text-lg">0{i + 1}</span>
              <div>
                <h3 className="font-bold mb-1">{title}</h3>
                <p className="text-sm text-gray-400">{desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Wasted Calculator (compact) — converts price into a "loss avoided" frame */}
      <section className="px-6 pb-12 max-w-3xl mx-auto" data-testid="snapshot-wasted-calc">
        <div className="bg-white text-gray-900 rounded-2xl">
          <WastedCalculator compact />
        </div>
        <p className="text-xs text-center text-gray-400 mt-3">
          €7 snapshot = mini-investícia oproti tomu, čo strácaš každý mesiac.
        </p>
      </section>

      {/* Risk reversal */}
      <section className="px-6 pb-16 max-w-3xl mx-auto" data-testid="snapshot-risk-reversal">
        <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-2xl p-6">
          <h3 className="font-black text-lg mb-2">14-dňová záruka vrátenia peňazí</h3>
          <p className="text-sm text-gray-300">
            Ak po prečítaní reportu nezistíte aspoň 3 konkrétne kroky ktoré môžete ihneď spraviť na zlepšenie AI viditeľnosti — napíšte nám a vrátime vám €7. Bez otázok.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24 max-w-5xl mx-auto">
        <OrderNowBottomCTA
          packageId="snapshot_tripwire"
          priceEur={7}
          interval="jednorazový"
          serviceName="AI Visibility Snapshot"
          headline="Objednaj si Snapshot za €7"
          subcopy="Jednorazová platba. Žiadne auto-renewal. 10-minútový self-serve audit s PDF reportom v inboxe."
          testid="snapshot-final-cta"
        />
      </section>

      {/* V228 — Exit-intent recovery popup (desktop only; mouseleave trigger).
          Shows once per session; onScan returns user to the URL input instead
          of the default "scroll to top" (which looks for hero-url-input on
          the homepage, not on this page). */}
      <ExitIntentPopup language="sk" onScan={handleExitScan} />
    </div>
  );
}

function TrustItem({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 text-center">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
}

